import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { FileText, Search, Eye, CheckCircle, XCircle, Clock, Upload, ChevronDown, ChevronUp, User } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { FileUpload } from './FileUpload';

interface Document {
  id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  category: string;
  uploaded_at: string;
  is_verified: boolean;
  status?: string;
}

interface StudentDocuments {
  student_id: string; // This will map to appointment id or student id
  visa_application_id: string; // Required for file upload
  student_name: string;
  application_number: string;
  country: string;
  status: string;
  documents: Document[];
  total_documents: number;
  verified_documents: number;
}

const CATEGORY_LABELS: Record<string, string> = {
  passport: 'Pasaport',
  photo: 'Fotoğraf',
  financial: 'Mali Belgeler',
  academic: 'Akademik Belgeler',
  accommodation: 'Konaklama',
  insurance: 'Sigorta',
  flight: 'Uçak Bileti',
  invitation: 'Davet Mektubu',
  other: 'Diğer'
};

export default function VisaDocumentManagement() {
  const [studentDocuments, setStudentDocuments] = useState<StudentDocuments[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedStudents, setExpandedStudents] = useState<Set<string>>(new Set());
  const [stats, setStats] = useState({
    total_students: 0,
    total_documents: 0,
    verified_documents: 0,
    pending_documents: 0
  });

  useEffect(() => {
    fetchStudentDocuments();
  }, []);

  const fetchStudentDocuments = async () => {
    try {
      setLoading(true);

      const { data: appointments, error } = await supabase
        .from('visa_appointments')
        .select(`
          id,
          full_name,
          preferred_country,
          status,
          passport_number,
          visa_application_id
        `)
        .eq('status', 'Kontrol');

      if (error) throw error;

      if (!appointments) {
        setStudentDocuments([]);
        return;
      }

      const mappedData: StudentDocuments[] = await Promise.all(appointments.map(async (apt: any) => {
        let mappedDocs: Document[] = [];

        if (apt.visa_application_id) {
          const { data: documents } = await supabase
            .from('visa_documents')
            .select('*')
            .eq('visa_application_id', apt.visa_application_id);

          if (documents) {
            mappedDocs = documents.map((doc: any) => ({
              id: doc.id,
              file_name: doc.document_name || doc.file_name,
              file_path: doc.file_path || doc.file_url,
              file_type: doc.file_type || 'application/pdf',
              category: doc.category || doc.document_type || 'other',
              uploaded_at: doc.uploaded_at || doc.created_at,
              is_verified: doc.status === 'verified' || doc.status === 'onaylandı',
              status: doc.status
            }));
          }
        }

        const totalDocs = mappedDocs.length;
        const verifiedDocs = mappedDocs.filter(d => d.is_verified).length;

        return {
          student_id: apt.id,
          visa_application_id: apt.visa_application_id,
          student_name: apt.full_name,
          application_number: apt.passport_number || '-',
          country: apt.preferred_country,
          status: apt.status,
          documents: mappedDocs,
          total_documents: totalDocs,
          verified_documents: verifiedDocs
        };
      }));

      setStudentDocuments(mappedData);
      calculateStats(mappedData);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data: StudentDocuments[]) => {
    const totalDocs = data.reduce((sum, student) => sum + student.total_documents, 0);
    const verifiedDocs = data.reduce((sum, student) => sum + student.verified_documents, 0);

    setStats({
      total_students: data.length,
      total_documents: totalDocs,
      verified_documents: verifiedDocs,
      pending_documents: totalDocs - verifiedDocs
    });
  };

  const toggleStudent = (studentId: string) => {
    const newExpanded = new Set(expandedStudents);
    if (newExpanded.has(studentId)) {
      newExpanded.delete(studentId);
    } else {
      newExpanded.add(studentId);
    }
    setExpandedStudents(newExpanded);
  };

  const handleDeleteStudent = async (studentId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (await window.appConfirm('Bu öğrenciyi listeden kaldırmak istediğinizden emin misiniz?')) {
      // Logic to remove from list? Or just local hide? 
      // Real deletion might be too aggressive. For now local hide or maybe update status?
      // User didn't specify deletion logic, so safe to just remove from view logic or warn.
      // But to be functional let's just update local state.
      const updatedStudents = studentDocuments.filter(s => s.student_id !== studentId);
      setStudentDocuments(updatedStudents);
      calculateStats(updatedStudents);
    }
  };

  const filteredStudents = studentDocuments.filter(student =>
    searchTerm === '' ||
    student.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.application_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      preparing: { label: 'Hazırlanıyor', className: 'bg-blue-100 text-blue-800' },
      submitted: { label: 'Başvuruldu', className: 'bg-yellow-100 text-yellow-800' },
      approved: { label: 'Onaylandı', className: 'bg-green-100 text-green-800' },
      rejected: { label: 'Reddedildi', className: 'bg-red-100 text-red-800' },
      pending_documents: { label: 'Belge Bekleniyor', className: 'bg-orange-100 text-orange-800' },
      Kontrol: { label: 'Kontrol Listesinde', className: 'bg-indigo-100 text-indigo-800' }
    };

    const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Belge Yönetimi</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Öğrencilerin vize belgelerini yönetin</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Toplam Öğrenci</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stats.total_students}</p>
            </div>
            <User className="w-10 h-10 text-gray-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Toplam Belge</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stats.total_documents}</p>
            </div>
            <FileText className="w-10 h-10 text-gray-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Onaylı Belge</p>
              <p className="text-3xl font-bold text-green-600 mt-2">{stats.verified_documents}</p>
            </div>
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Bekleyen Belge</p>
              <p className="text-3xl font-bold text-orange-600 mt-2">{stats.pending_documents}</p>
            </div>
            <Clock className="w-10 h-10 text-orange-400" />
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Öğrenci adı veya başvuru no ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>

        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {loading ? (
            <div className="p-12 text-center text-gray-500 dark:text-gray-400">
              Yükleniyor...
            </div>
          ) : filteredStudents.length === 0 ? (
            <div className="p-12 text-center text-gray-500 dark:text-gray-400">
              Öğrenci bulunamadı
            </div>
          ) : (
            filteredStudents.map((student) => (
              <div key={student.student_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <div
                  onClick={() => toggleStudent(student.student_id)}
                  className="p-6 cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-full bg-[#0c9346] flex items-center justify-center text-white font-semibold text-lg">
                          {student.student_name ? student.student_name.split(' ').map(n => n[0]).join('') : '?'}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {student.student_name}
                          </h3>
                          {getStatusBadge(student.status)}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-600 dark:text-gray-400">
                          <span>{student.application_number}</span>
                          <span>•</span>
                          <span>{student.country}</span>
                          <span>•</span>
                          <span>{student.total_documents} Belge</span>
                          <span>•</span>
                          <span className="text-green-600 dark:text-green-400">
                            {student.verified_documents} Onaylı
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg">
                        <Upload className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                      </button>
                      <button
                        onClick={(e) => handleDeleteStudent(student.student_id, e)}
                        className="p-2 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg group"
                        title="Öğrenciyi Sil"
                      >
                        <XCircle className="w-5 h-5 text-gray-400 group-hover:text-red-500 transition-colors" />
                      </button>
                      {expandedStudents.has(student.student_id) ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {expandedStudents.has(student.student_id) && (
                  <div className="px-6 pb-6 bg-gray-50 dark:bg-gray-900/50">
                    <div className="mb-6 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                      <h4 className="text-md font-semibold mb-4 text-gray-900 dark:text-white">Yeni Belge Ekle</h4>
                      {student.visa_application_id ? (
                        <FileUpload
                          visaApplicationId={student.visa_application_id}
                          onUploadComplete={fetchStudentDocuments}
                        />
                      ) : (
                        <div className="text-red-500">
                          Bu başvuru için vize dosya kaydı oluşturulmamış (Visa Application ID eksik).
                        </div>
                      )}
                    </div>

                    <h4 className="text-md font-semibold mb-2 text-gray-900 dark:text-white">Mevcut Belgeler</h4>
                    {student.documents.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                        Henüz belge yüklenmemiş
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {student.documents.map((doc) => (
                          <div
                            key={doc.id}
                            className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                          >
                            <div className="flex items-center gap-3 flex-1">
                              <FileText className="w-5 h-5 text-gray-400" />
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                  {doc.file_name}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {CATEGORY_LABELS[doc.category] || doc.category} • {doc.uploaded_at ? format(new Date(doc.uploaded_at), 'dd MMM yyyy', { locale: tr }) : '-'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {doc.is_verified ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <CheckCircle className="w-3 h-3" />
                                  Onaylı
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                  <Clock className="w-3 h-3" />
                                  Beklemede
                                </span>
                              )}
                              <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                                <Eye className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
